import { createContext, type ReactNode, useCallback, useContext } from 'react';

import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import type { SecondaryFilterConfig } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import { MULTI_FILTER_ID_BY_FILTER_ID } from '../engine/filterRegistry';
import type { FilterId } from '../types/filters';
import { isExpressionDimension } from '../utils/filters-v2/expression-dimensions';

/**
 * Every request these pages make takes the expression as its `filters_v2`:
 * `postings_v3` on the postings pages, `transitions_v3` on the transitions
 * pages.
 */
const FILTER_EXPRESSION_PAGE_IDS: ReadonlySet<string> = new Set([
  RevelioPageId.COMPANY_POSTINGS,
  RevelioPageId.INDUSTRY_POSTINGS,
  RevelioPageId.GEOGRAPHY_POSTINGS,
  RevelioPageId.ROLE_POSTINGS,
  RevelioPageId.SKILL_POSTINGS,
  RevelioPageId.ACTIVITY_POSTINGS,
  RevelioPageId.COMPANY_TRANSITIONS,
  RevelioPageId.INDUSTRY_TRANSITIONS,
  RevelioPageId.GEOGRAPHY_TRANSITIONS,
  RevelioPageId.ROLE_TRANSITIONS,
]);

/** Whether a page's filters combine as Talent Discovery's do. */
export const useIsFilterExpressionPage = (): ((
  revelioPageId: RevelioPageId | undefined
) => boolean) => {
  const isEnabled = useUnleashFlag(FeatureFlag.FilterExpressions);
  return useCallback(
    (revelioPageId) =>
      isEnabled &&
      revelioPageId !== undefined &&
      FILTER_EXPRESSION_PAGE_IDS.has(revelioPageId),
    [isEnabled]
  );
};

const FilterExpressionsContext = createContext(false);

/**
 * Decides once per page whether its filters combine as Talent Discovery's do.
 * Only the built-in pages: a custom dashboard can carry the same page id, but
 * its filter list editor cannot hold multi filters.
 */
export const FilterExpressionsProvider = ({
  page,
  children,
}: {
  page: Page;
  children: ReactNode;
}) => {
  const isFilterExpressionPage = useIsFilterExpressionPage();
  const usesFilterExpressions =
    page.type === PageType.REVELIO_PUBLIC &&
    isFilterExpressionPage(page.revelioPageId);
  return (
    <FilterExpressionsContext.Provider value={usesFilterExpressions}>
      {children}
    </FilterExpressionsContext.Provider>
  );
};

/** False outside `FilterExpressionsProvider`, so other surfaces keep `filters`. */
export const useFilterExpressions = (): boolean =>
  useContext(FilterExpressionsContext);

/**
 * A page's secondary filters with the entity ones as multi filters, as on
 * Talent Discovery, so each can be added more than once. The page's own
 * entity stays single: it is the primary, not a filter.
 */
export const withMultiEntityFilters = (
  secondaryFilters: SecondaryFilterConfig[],
  primaryFilterId: FilterId | undefined
): SecondaryFilterConfig[] =>
  secondaryFilters.map((secondaryFilter) => {
    const multiFilterId = MULTI_FILTER_ID_BY_FILTER_ID[secondaryFilter.id];
    return multiFilterId !== undefined &&
      secondaryFilter.id !== primaryFilterId &&
      isExpressionDimension(secondaryFilter.id)
      ? { ...secondaryFilter, id: multiFilterId }
      : secondaryFilter;
  });
